import { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/highcharts-more";

export type Group = {
  title: string;
  value?: number;
  std?: number;
  box?: {
    min: number;
    q1: number;
    median: number;
    q3: number;
    max: number;
  } | null;
  percentile?: number;
  color?: string;
};

type Props = {
  groups: Group[];
  height?: number;
  bounds?: { min: number; max: number };
};

Highcharts.SVGRenderer.prototype.symbols.triangleLeft = (x: number, y: number, w: number, h: number) => {
  // x, y: top-left corner of the bounding box
  // w, h: width and height of the bounding box
  
  // Define the path for a sideways triangle (pointing right)
  return [
    'M', x, y + h / 2,         // Move to the left middle point
    'L', x + w, y,             // Line to the top right point
    'L', x + w, y + h,         // Line to the bottom right point
    'Z'                        // Close the path
  ];
};

export default function BoxPlot({
  groups,
  height = 300,
  bounds = { min: 0, max: 100 },
}: Props) {
  const categories = useMemo(
    () => groups.map(g => g.title),
    [groups]
  );

  /* Add padding to bounds for better visualization */
  const pad = Math.max(0.5, 0.01 * (bounds.max - bounds.min));

  /* Create data for box plot, scatter plot, and error bars */
  const { boxData, pointData, errorBarData } = useMemo(() => {
    const boxData: Highcharts.SeriesBoxplotOptions['data'] = [];
    const pointData: Highcharts.SeriesScatterOptions['data'] = [];
    const errorBarData: Highcharts.SeriesErrorbarOptions['data'] = [];
    
    /* Keep numbers within bounds */
    const clamp = (v: number) => Math.max(bounds.min, Math.min(bounds.max, v));

    groups.forEach((g, i) => {
      /* Box plot */
      if (g.box) {
        const { min, q1, median, q3, max } = g.box;
        boxData.push({
          x: i,
          low: clamp(min),
          q1: clamp(q1),
          median: clamp(median),
          q3: clamp(q3),
          high: clamp(max),
          name: g.title,
          fillColor: g.color,
        });
      }

      /* Single value + optional std as error bars */
      if (g.value) {
        const y = clamp(g.value);
        pointData.push({
          x: i,
          y,
          name: g.title,
          color: g.color,
        });

        if (g.std) {
          const low = clamp(y - g.std);
          const high = clamp(y + g.std);
          errorBarData.push({
            x: i,
            low,
            high,
            name: g.title,
            color: g.color,
          });
        }

        // scatterMarkerColor = getColorGradient(g.percentile!).backgroundColor;
      }
    });

    return { boxData, pointData, errorBarData };
  }, [groups, bounds.min, bounds.max]);

  /* Chart options */
  const options: Highcharts.Options = {
    chart: {
      height,
      backgroundColor: "#2b2b2b",
    },
    title: { text: undefined },
    xAxis: {
      categories,
      tickLength: 0,
      labels: {
        style: {
          fontSize: '16px',
          color: "#eef2f8",
        }
      }
    },
    yAxis: {
      title: {
        text: "Test Score",
        style: {
          fontSize: '18px',
          color: "#eef2f8",
        }
      },
      labels: {
        style: {
          fontSize: '16px',
          color: "#eef2f8",
        }
      },
      min: bounds.min - pad,
      max: bounds.max + pad,
      startOnTick: false,
      endOnTick: false,
      gridLineWidth: 2,
      gridLineColor: "#3e3e3e",
    },
    legend: { enabled: false },
    tooltip: {
      shared: true,  // This makes one tooltip per x-axis value
      useHTML: true,
      hideDelay: 0,
      outside: false,
      // followPointer: true,  // Make tooltip follow the mouse
      // followTouchMove: true,  // Also follow on touch devices

      /* Position the tooltip slightly offset from the cursor */
      // positioner: function (labelWidth: any, labelHeight: any, point: any) {
      //   return {
      //     x: (point.plotX || 0) - labelWidth / 2,
      //     y: (point.plotY || 0) - labelHeight - 10
      //   };
      // },
      formatter: function (this: any) {
        const points = this.points || [this];
        const point = points[0];
        const x = typeof point.x === 'number' ? point.x : 0;
        
        const boxPoint = points.find((p: any) => p.series.type === 'boxplot');
        const errorPoint = points.find((p: any) => p.series.type === 'errorbar');
        const scatterPoint = points.find((p: any) => p.series.type === 'scatter');
        
        let tooltip = `<div style="z-index: 1000"><b>${categories[x]}</b>`;
        
        if (boxPoint) {
          tooltip += `<br/>Max: ${boxPoint.high?.toFixed(2)}`;
          tooltip += `<br/>Q3: ${boxPoint.q3?.toFixed(2)}`;
          tooltip += `<br/>Median: ${boxPoint.median?.toFixed(2)}`;
          tooltip += `<br/>Q1: ${boxPoint.q1?.toFixed(2)}`;
          tooltip += `<br/>Min: ${boxPoint.low?.toFixed(2)}`;
        }
        
        if (scatterPoint) {
          tooltip += `<br/>Value: ${scatterPoint.y?.toFixed(2)}`;
        }
        
        if (errorPoint) {
          tooltip += `<br/>Std range: ${errorPoint.low?.toFixed(2)} – ${errorPoint.high?.toFixed(2)}`;
        }

        const pct = groups[x]?.percentile;
        if (typeof pct === 'number' && Number.isFinite(pct)) {
          tooltip += `<br/>Percentile: ${pct.toFixed(2)}`;
        }
        
        tooltip += '</div>';
        return tooltip;
      }
    },
    plotOptions: {
      // series: {
      //   animation: false,
      //   states: { hover: { enabled: true } },
      // },
      scatter: {
        marker: {
          symbol: "triangleLeft",
          radius: 8,
          // fillColor: (point: any) => point.color || '#000000',
        },
        stickyTracking: false,
      },
      errorbar: {
        // color: (point: any) => point.color || '#000000',
        lineWidth: 2,
        whiskerWidth: 2,
        whiskerLength: "40%",
        stickyTracking: false,
      },
      boxplot: {
        // color: (point: any) => point.color || '#000000',
        lineWidth: 2,
        medianWidth: 2,
        whiskerWidth: 2,
        whiskerLength: "50%",
        boxDashStyle: 'Solid',
        fillColor: "#3a86ff",
        // whiskerColor: "#4a96ff",
        color: '#42e4ed',
        stickyTracking: false,
      },
    },
    series: [
      // Box plots (if any)
      ...(boxData.length
        ? [{
            type: "boxplot",
            name: "Box",
            data: boxData,
            tooltip: { pointFormat: "" },
            stickyTracking: true,
            states: {
              hover: {
                enabled: true,
                brightness: 1,
              }
            }
          } as Highcharts.SeriesBoxplotOptions]
        : []),
      // Points (values)
      ...(pointData.length
        ? [{
            type: "scatter",
            name: "Value",
            data: pointData,
            clip: false,
            stickyTracking: true,
            states: {
              hover: {
                enabled: true,
                halo: {
                  size: 10,
                  opacity: 1
                }
              }
            }
          } as Highcharts.SeriesScatterOptions]
        : []),
      // Error bars paired with points
      ...(errorBarData.length
        ? [{
            type: "errorbar",
            name: "Std Dev",
            data: errorBarData,
            stickyTracking: true,
            states: {
              hover: {
                enabled: true
              }
            }
          } as Highcharts.SeriesErrorbarOptions]
        : []),
    ] as Highcharts.SeriesOptionsType[],
    credits: { enabled: false },
  };

  return (
    <>
      <HighchartsReact highcharts={Highcharts} options={options} />
      {groups
        .filter((g) => g.box === null)
        .map((g) => (
          <p key={g.title}>No data for {g.title} with the current filters.</p>
        ))}
    </>
  );
}
